import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const FeaturesSection = () => {
  const { t, isRTL } = useLanguage();

  const features = [
    {
      icon: "fas fa-hard-hat",
      title: isRTL ? "خبرة عقود البناء" : "Construction Contract Expertise",
      description: isRTL ? "متخصص في معايير FIDIC، قوانين البناء في دول مجلس التعاون الخليجي، ولوائح الشراء المحلية." : "Specialized in FIDIC standards, GCC construction laws, and local procurement regulations.",
      points: isRTL ? [
        "كتب FIDIC الأحمر والأصفر والفضي",
        "قوانين البناء في دول مجلس التعاون الخليجي والإمارات"
      ] : [
        "FIDIC Red, Yellow, Silver Books",
        "GCC and UAE construction laws"
      ]
    },
    {
      icon: "fas fa-exclamation-triangle",
      title: isRTL ? "منع المخاطر والنزاعات" : "Risk & Dispute Prevention",
      description: isRTL ? "تحديد النزاعات القانونية المحتملة قبل حدوثها، توفير الملايين في تكاليف المشاريع." : "Identify potential legal disputes before they happen, saving millions in project costs.",
      points: isRTL ? [
        "تحليل غرامات التأخير",
        "الامتثال لضمانات الأداء"
      ] : [
        "Delay penalty analysis",
        "Performance security compliance"
      ]
    },
    {
      icon: "fas fa-comments",
      title: isRTL ? "اسأل جيلاني الذكي" : "Ask Jelani AI",
      description: isRTL ? "احصل على إجابات فورية حول عقود البناء مع مساعدنا الذكي المتخصص." : "Get instant answers about construction contracts with our specialized AI assistant.",
      points: isRTL ? [
        "استفسارات قانون البناء",
        "شروحات بنود FIDIC"
      ] : [
        "Construction law queries",
        "FIDIC clause explanations"
      ]
    },
    {
      icon: "fas fa-language",
      title: isRTL ? "دعم العربية والإنجليزية" : "Arabic & English Support",
      description: isRTL ? "دعم كامل ثنائي اللغة لعقود البناء ولوائح الشرق الأوسط." : "Full bilingual support for Middle Eastern construction contracts and regulations.",
      points: isRTL ? [
        "تحليل العقود العربية",
        "تكامل القوانين المحلية"
      ] : [
        "Arabic contract analysis",
        "Local law integration"
      ]
    },
    {
      icon: "fas fa-users",
      title: isRTL ? "من يستفيد" : "Who Benefits",
      description: isRTL ? "مبني لمهنيي البناء والفرق القانونية والاستشاريين الهندسيين." : "Built for construction professionals, legal teams, and engineering consultants.",
      points: isRTL ? [
        "المهندسون والمقاولون",
        "الفرق القانونية والاستشاريين"
      ] : [
        "Engineers & Contractors ",
        "Legal Teams & Consultants"
      ]
    },
    {
      icon: "fas fa-chart-line",
      title: isRTL ? "كفاءة التكلفة" : "Cost Efficiency",
      description: isRTL ? "قلل التكاليف القانونية بنسبة 95% مع الحفاظ على الدقة والاتساق في مراجعة العقود." : "Reduce legal costs by 95% while maintaining accuracy and consistency in contract review.",
      points: isRTL ? [
        "2,000 درهم إماراتي مقابل 85,000 درهم إماراتي",
        "دقائق مقابل أيام في وقت المراجعة"
      ] : [
        "AED 2,000 vs AED 85,000",
        "Minutes vs Days review time"
      ]
    }
  ];

  return (
    <section className="py-20 bg-gray-50" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {isRTL ? 'حول عملية مراجعة عقود البناء' : 'Transform Your Construction Contract Review Process'}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {isRTL ? 
  'يجمع جيلاني الذكي بين الذكاء الاصطناعي وخبرة قانون البناء، مدرب على معايير FIDIC وعقود الشرق الأوسط ولوائح دول مجلس التعاون الخليجي لمساعدتك في مراجعة العقود بشكل أسرع وأكثر دقة.' :
  'Jelani AI combines artificial intelligence with construction law expertise, trained on FIDIC standards, Middle East contract, and GCC regulations to help you review contracts faster and more accurately.'
}
          </p>
          <div className={`mt-6 flex justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-8 text-sm text-gray-500`}>
            <div className="flex items-center">
              <i className={`fas fa-clock ${isRTL ? 'ml-2' : 'mr-2'} text-blue-600`}></i>
              <span>{isRTL ? 'متاح 24/7' : '24/7 Availability'}</span>
            </div>
            <div className="flex items-center">
              <i className={`fas fa-shield-alt ${isRTL ? 'ml-2' : 'mr-2'} text-green-600`}></i>
              <span>{isRTL ? '95% تخفيض التكلفة' : '95% Cost Reduction'}</span>
            </div>
            <div className="flex items-center">
              <i className={`fas fa-balance-scale ${isRTL ? 'ml-2' : 'mr-2'} text-amber-600`}></i>
              <span>{isRTL ? 'مدرب على FIDIC' : 'FIDIC Trained'}</span>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="feature-card bg-white p-8 rounded-xl shadow-md border border-gray-100 transition-all duration-300 staggered-animation">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className={`${feature.icon} text-blue-600 text-2xl`}></i>
              </div>
              <h3 className={`text-xl font-semibold text-gray-900 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>{feature.title}</h3>
              <p className={`text-gray-600 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{feature.description}</p>
              <ul className="space-y-2 ">
                {feature.points.map((point, pointIndex) => (
                  <li key={pointIndex} className="flex items-start">
                    <i className={`fas fa-check text-green-500 mt-1 ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    <span className={`text-blue-600 ${isRTL ? 'text-right' : 'text-left'}`}>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
